// Fallback UI for the Supabase auth email-confirmation / password-recovery
// redirect (https://invite.daruvix.com/auth/callback). This page is only
// ever SEEN when the Android/iOS App Link handoff did not happen
// automatically (app not installed, verification not yet propagated, a
// desktop browser, etc.) — when the App Link is verified and the app is
// installed, the OS hands the URL straight to Daruvix before this page
// ever renders.
//
// Deliberately does nothing with the token beyond forwarding it opaquely:
// no fetch, no parsing, no display of its value anywhere in the DOM. The
// only consumer of the token is the Daruvix app itself, once it receives
// the deep link (see lib/auth/auth_callback.dart).

export function isAuthCallbackPath(pathname: string): boolean {
  return pathname.replace(/\/+$/, "") === "/auth/callback";
}

// The legacy custom scheme is deliberately kept registered in the
// Android manifest for exactly this: a direct, user-initiated tap on a
// custom-scheme link is reliably handed to the OS by every browser —
// it's only a *server-issued redirect* to a custom scheme (the original
// failure mode) that's unreliable. Preserves the query + hash verbatim
// and untouched; never reads their contents.
function buildLegacyAppLink(): string {
  return `daruvix://auth/callback${window.location.search}${window.location.hash}`;
}

export function renderAuthCallback(root: HTMLElement): void {
  root.setAttribute("aria-live", "polite");
  root.innerHTML = "";

  const card = el("div", "card");
  card.appendChild(el("div", "logo", "Daruvix"));

  const content = el("div", "content");
  content.appendChild(el("h1", "heading", "Email confirmed. Opening Daruvix…"));
  content.appendChild(
    el(
      "p",
      "body",
      "If Daruvix didn't open automatically, tap below to continue.",
    ),
  );

  const ctas = el("div", "ctas");
  const openLink = el("a", "btn btn-primary", "Open Daruvix") as HTMLAnchorElement;
  openLink.href = buildLegacyAppLink();
  ctas.appendChild(openLink);
  content.appendChild(ctas);

  card.appendChild(content);
  card.appendChild(
    el("p", "privacy-note", "No medical information is shown on this page."),
  );
  root.appendChild(card);
}

function el(tag: string, className: string, text?: string): HTMLElement {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}
